import React from 'react';
import {
  Card,
  Flex,
  Icon,
  Text,
  Collapse,
  Box,
  Button,
  AbstractButton,
  Spinner,
  useSnackbar,
  TextInput,
} from 'pounceV2';
import GenericCodeBlockV3 from 'Components/CodeBlock/GenericCodeBlockV3';
import { generateDetectionDiff } from 'Pages/Build/Detections/Rules/SimpleRuleForm/CopilotSection/diffUtils';
import DiffHunkCardV3 from 'Pages/Build/Detections/Rules/SimpleRuleForm/CopilotSection/DiffHunkCardV3';
import { AIToolInvocation } from 'Generated/schema';
import { extractErrorMessage } from 'Helpers/utils';
import { formatSchedule } from 'Helpers/scheduledPrompts';
import { useTheme } from '@mui/material';
import {
  DETECTIONS_CREATE,
  DETECTIONS_UPDATE,
  ALERTS_UPDATE,
  ALERTS_ASSIGN,
  ALERTS_BULK_UPDATE,
  ALERTS_ADD_COMMENT,
  UTILITIES_FETCH_WEB,
  UTILITIES_ASK_QUESTION,
  DATALAKE_WRITE_SAVED_QUERY,
  NOTIFICATIONS_SLACK_SEND_MESSAGE,
} from 'Generated/aiToolNames.generated';
import CollapsibleFieldV3 from 'Components/CollapsibleText/CollapsibleFieldV3';
import { useDecideToolInvocation } from './graphql/decideToolInvocation.generated';
import { useGenericAISummaryContextV3 } from './GenericAISummaryContextV3';
import { useToolInvocationContext } from './ToolInvocationContext';

const BREATHING_ANIMATION_DURATION_MS = 5000;

const normalizeContextTags = (value: unknown): string[] | undefined => {
  if (Array.isArray(value)) {
    return value as string[];
  }
  if (typeof value === 'string') {
    return [value];
  }
  return undefined;
};

/**
 * Column-layout metadata item matching DetectionReviewCardV3 style,
 * with optional old→new change indicator for the update path.
 */
const MetadataItemWithChange = ({
  label,
  value,
  oldValue,
  suffix,
  mono,
}: {
  label: string;
  value: any;
  oldValue?: any;
  suffix?: string;
  mono?: boolean;
}) => {
  const {
    palette: { tokens },
  } = useTheme();

  if (value == null) {
    return null;
  }

  const displayValue = suffix ? `${value} ${suffix}` : value;
  const changed = oldValue != null && String(oldValue) !== String(value);

  return (
    <Box display="flex" flexDirection="column" gap={1} pr={6} pt={1}>
      <Text variant="body/sm" color={tokens.content.text.neutral[2]}>
        {label}
      </Text>
      {changed ? (
        <Flex align="center" gap={1}>
          <Text
            variant="body/sm"
            fontFamily={mono ? 'monospace' : undefined}
            sx={{ textDecoration: 'line-through', opacity: 0.6 }}
          >
            {suffix ? `${oldValue} ${suffix}` : oldValue}
          </Text>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            →
          </Text>
          <Text
            variant="body/sm"
            fontFamily={mono ? 'monospace' : undefined}
            color={tokens.content.text.semantic.suggest.base}
          >
            {displayValue}
          </Text>
        </Flex>
      ) : (
        <Text
          as="div"
          variant="body/sm"
          fontFamily={mono ? 'monospace' : undefined}
          color={tokens.content.text.neutral.base}
        >
          {displayValue}
        </Text>
      )}
    </Box>
  );
};

const UpdateAlertContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const alertId = metadata?.alert_id;
  const status = metadata?.status;
  const quality = metadata?.quality;
  const contextTags = normalizeContextTags(metadata?.context_tags);
  const comment = metadata?.comment;

  return (
    <Card gap={2} p={3} variant="midToBrand">
      <Flex align="center" gap={1}>
        <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
          Alert:
        </Text>
        <Text variant="body/sm" fontWeight="bold" fontFamily="monospace">
          {alertId}
        </Text>
      </Flex>
      {status && (
        <Flex align="center" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            New status:
          </Text>
          <Text variant="body/sm" fontWeight="bold">
            {status}
          </Text>
        </Flex>
      )}
      {quality && (
        <Flex align="center" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Quality:
          </Text>
          <Text variant="body/sm" fontWeight="bold">
            {quality}
          </Text>
        </Flex>
      )}
      {contextTags?.length > 0 && (
        <Flex align="center" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Context tags:
          </Text>
          <Text variant="body/sm" fontWeight="bold">
            {contextTags.join(', ')}
          </Text>
        </Flex>
      )}
      {comment && (
        <Flex direction="column" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Comment:
          </Text>
          <Text variant="body/sm" fontStyle="italic">
            {comment}
          </Text>
        </Flex>
      )}
    </Card>
  );
};

const AssignAlertContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const alertId = metadata?.alert_id;
  const assigneeName = metadata?.assignee_name || metadata?.assignee_id;
  const comment = metadata?.comment;

  return (
    <Card gap={2} p={3} variant="midToBrand">
      <Flex align="center" gap={1}>
        <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
          Alert:
        </Text>
        <Text variant="body/sm" fontWeight="bold" fontFamily="monospace">
          {alertId}
        </Text>
      </Flex>
      <Flex align="center" gap={1}>
        <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
          Assign to:
        </Text>
        <Text variant="body/sm" fontWeight="bold">
          {assigneeName}
        </Text>
      </Flex>
      {comment && (
        <Flex direction="column" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Comment:
          </Text>
          <Text variant="body/sm" fontStyle="italic">
            {comment}
          </Text>
        </Flex>
      )}
    </Card>
  );
};

const BulkUpdateAlertsContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const alertIds = metadata?.alert_ids || [];
  const status = metadata?.status;
  const quality = metadata?.quality;
  const contextTags = normalizeContextTags(metadata?.context_tags);
  const assigneeName = metadata?.assignee_name || metadata?.assignee_id;
  const hasAssignee = metadata?.assignee_id;
  const comment = metadata?.comment;

  return (
    <Flex direction="column" gap={3}>
      <Card gap={2} p={3} variant="midToBrand">
        <Flex align="center" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Alerts to update:
          </Text>
          <Text variant="body/sm" fontWeight="bold">
            {alertIds.length}
          </Text>
        </Flex>
        {status && (
          <Flex align="center" gap={1}>
            <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
              New status:
            </Text>
            <Text variant="body/sm" fontWeight="bold">
              {status}
            </Text>
          </Flex>
        )}
        {quality && (
          <Flex align="center" gap={1}>
            <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
              Quality:
            </Text>
            <Text variant="body/sm" fontWeight="bold">
              {quality}
            </Text>
          </Flex>
        )}
        {contextTags?.length > 0 && (
          <Flex align="center" gap={1}>
            <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
              Context tags:
            </Text>
            <Text variant="body/sm" fontWeight="bold">
              {contextTags.join(', ')}
            </Text>
          </Flex>
        )}
        {hasAssignee && (
          <Flex align="center" gap={1}>
            <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
              Assign to:
            </Text>
            <Text variant="body/sm" fontWeight="bold">
              {assigneeName}
            </Text>
          </Flex>
        )}
        {comment && (
          <Flex direction="column" gap={1}>
            <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
              Comment to add:
            </Text>
            <Text variant="body/sm" fontStyle="italic">
              {comment}
            </Text>
          </Flex>
        )}
      </Card>
      <Box
        maxHeight="150px"
        overflowY="auto"
        p={2}
        bg={tokens.background.brand.dark.base}
        borderRadius="small"
      >
        <Text variant="label/sm" color={tokens.content.text.neutral[3]} mb={1}>
          Alert IDs:
        </Text>
        <Text variant="label/sm" fontFamily="monospace" whiteSpace="pre-wrap" wordBreak="break-all">
          {alertIds.join(', ')}
        </Text>
      </Box>
    </Flex>
  );
};

const AddAlertCommentContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const alertId = metadata?.alert_id;
  const comment = metadata?.comment;

  return (
    <Card gap={2} p={3} variant="midToBrand">
      <Flex align="center" gap={1}>
        <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
          Alert:
        </Text>
        <Text variant="body/sm" fontWeight="bold" fontFamily="monospace">
          {alertId}
        </Text>
      </Flex>
      <Flex direction="column" gap={1}>
        <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
          Comment:
        </Text>
        <Text variant="body/sm" fontStyle="italic">
          {comment}
        </Text>
      </Flex>
    </Card>
  );
};

const SlackSendMessageContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const channel = metadata?.channel;
  const message = metadata?.message;
  const threadTs = metadata?.thread_ts;

  return (
    <Card gap={2} p={3} variant="midToBrand">
      {channel && (
        <Flex align="center" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Channel:
          </Text>
          <Text variant="body/sm" fontWeight="bold" fontFamily="monospace">
            {channel}
          </Text>
        </Flex>
      )}
      {threadTs && (
        <Flex align="center" gap={1}>
          <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
            Replying in thread:
          </Text>
          <Text variant="body/sm" fontFamily="monospace">
            {threadTs}
          </Text>
        </Flex>
      )}
      <Flex direction="column" gap={1}>
        <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
          Message:
        </Text>
        <Text variant="body/sm" fontStyle="italic" whiteSpace="pre-wrap">
          {message}
        </Text>
      </Flex>
    </Card>
  );
};

const GenericToolInputContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();

  if (!metadata || Object.keys(metadata).length === 0) {
    return (
      <Text variant="body/sm" color={tokens.content.text.neutral[3]}>
        No input parameters
      </Text>
    );
  }

  return (
    <Flex direction="column" gap={2}>
      <Text variant="label/sm" color={tokens.content.text.neutral[3]}>
        Tool Input
      </Text>
      <Box maxHeight="200px" overflowY="auto" p={3} borderRadius="medium">
        <GenericCodeBlockV3 language="json">{JSON.stringify(metadata, null, 2)}</GenericCodeBlockV3>
      </Box>
    </Flex>
  );
};

// URL is intentionally rendered as plain text (not a link) to defang it —
// users should review the URL before approving the fetch.
const WebContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();

  return (
    <Flex direction="column" gap={3}>
      <Text color={tokens.content.text.neutral[3]} variant="body/sm">
        The AI wants to fetch content from the following URL:
      </Text>
      <Box p={3} bg={tokens.background.brand.dark.base} borderRadius="medium">
        <Text variant="body/sm" fontFamily="monospace" wordBreak="break-all">
          {metadata?.url || 'No URL provided'}
        </Text>
      </Box>
    </Flex>
  );
};

const SavedQueryContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const queryType = metadata?.type;
  const sql = metadata?.sql;
  const schedulePeriod = metadata?.schedule_period_minutes;
  const scheduleCron = metadata?.schedule_cron_expression;
  const scheduleTimeout = metadata?.schedule_timeout_minutes;
  const isScheduled = queryType === 'scheduled';

  const scheduleDisplay = React.useMemo(() => {
    if (!isScheduled) {
      return null;
    }
    return formatSchedule({
      rateMinutes: schedulePeriod ? Number(schedulePeriod) : null,
      cronExpression: scheduleCron || null,
    });
  }, [isScheduled, schedulePeriod, scheduleCron]);

  return (
    <Card gap={4} p={4} variant="midToBrand">
      <Flex flexWrap="wrap" gap={4}>
        <MetadataItemWithChange
          label="Type"
          value={isScheduled ? 'Scheduled Query' : 'Saved Query'}
        />
        {isScheduled && scheduleDisplay && (
          <MetadataItemWithChange label="Schedule" value={scheduleDisplay} />
        )}
        {isScheduled && scheduleTimeout && (
          <MetadataItemWithChange label="Timeout" value={`${scheduleTimeout} minutes`} />
        )}
      </Flex>
      {metadata?.description && (
        <Box mt={2}>
          <Text variant="body/sm" color={tokens.content.text.neutral[2]} mb={1}>
            Description
          </Text>
          <Text variant="body/sm">{metadata.description}</Text>
        </Box>
      )}
      {sql && <GenericCodeBlockV3 language="sql">{sql}</GenericCodeBlockV3>}
    </Card>
  );
};

const formatEnabledStatus = (enabled: boolean) => (enabled ? 'Enabled' : 'Disabled');

const DetectionContent = ({ metadata }: { metadata: any }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const [showCode, setShowCode] = React.useState(true);
  const existing = metadata?._existing;
  const isUpdate = !!existing;
  const threshold = metadata?.threshold ?? metadata?.alertEventThreshold;
  const codeDiff = React.useMemo(
    () =>
      existing?.body && metadata?.body ? generateDetectionDiff(existing.body, metadata.body) : null,
    [existing?.body, metadata?.body],
  );

  const totalTests = metadata?.tests?.length || 0;
  const hasTopRow =
    metadata?.enabled != null ||
    metadata?.logTypes ||
    metadata?.scheduledQueries ||
    metadata?.severity != null;
  const hasBottomRow = metadata?.dedupPeriodMinutes != null || threshold != null || totalTests > 0;

  // Metadata box — shared by both create and update paths.
  // When existing is present (update), MetadataItemWithChange shows old→new diffs.
  // When existing is null (create), oldValue is undefined and items render without diffs.
  const metadataBox = React.useMemo(
    () =>
      (hasTopRow || hasBottomRow) && (
        <Box
          borderWidth="1px"
          borderStyle="solid"
          borderColor={tokens.stroke.neutral[5]}
          borderRadius="large"
          overflow="hidden"
        >
          {hasTopRow && (
            <Flex gap={10} align="baseline" p={3} flexWrap="wrap">
              <MetadataItemWithChange
                label="Status"
                value={metadata?.enabled != null ? formatEnabledStatus(metadata.enabled) : null}
                oldValue={existing?.enabled != null ? formatEnabledStatus(existing.enabled) : null}
              />
              <MetadataItemWithChange
                label="Log Types"
                value={Array.isArray(metadata?.logTypes) ? metadata.logTypes.join(', ') : null}
                oldValue={Array.isArray(existing?.logTypes) ? existing.logTypes.join(', ') : null}
              />
              <MetadataItemWithChange
                label="Scheduled Queries"
                value={
                  Array.isArray(metadata?.scheduledQueries)
                    ? metadata.scheduledQueries.join(', ')
                    : null
                }
                oldValue={
                  Array.isArray(existing?.scheduledQueries)
                    ? existing.scheduledQueries.join(', ')
                    : null
                }
              />
              <MetadataItemWithChange
                label="Severity"
                value={metadata?.severity}
                oldValue={existing?.severity}
              />
            </Flex>
          )}
          {hasTopRow && hasBottomRow && (
            <Box height="1px" backgroundColor={tokens.stroke.neutral[5]} />
          )}
          {hasBottomRow && (
            <Flex gap={10} align="center" p={3} flexWrap="wrap">
              <MetadataItemWithChange
                label="Dedupe"
                value={metadata?.dedupPeriodMinutes}
                oldValue={existing?.dedupPeriodMinutes}
                suffix="min"
              />
              <MetadataItemWithChange
                label="Threshold"
                value={threshold}
                oldValue={existing?.threshold ?? existing?.alertEventThreshold}
              />
              {totalTests > 0 && (
                <MetadataItemWithChange label="Tests" value={`${totalTests} included`} />
              )}
            </Flex>
          )}
        </Box>
      ),
    [metadata, existing, tokens, hasTopRow, hasBottomRow, threshold, totalTests],
  );

  if (isUpdate) {
    return (
      <>
        {metadataBox}
        {(['description', 'runbook', 'reference'] as const).map(field =>
          metadata?.[field] != null && metadata[field] !== existing?.[field] ? (
            <CollapsibleFieldV3 key={field} label={`Updated ${field}`} value={metadata[field]} />
          ) : null,
        )}
        {metadata?.body != null && (
          <Box overflow="hidden">
            <AbstractButton onClick={() => setShowCode(!showCode)}>
              <Icon type={showCode ? 'chevron-down' : 'chevron-right'} size="small" />
              {showCode ? 'Hide' : 'Show'} {codeDiff ? 'Code Changes' : 'Detection Code'}
            </AbstractButton>
            <Collapse isOpen={showCode}>
              <Box mt={2}>
                {codeDiff ? (
                  codeDiff.hunks.map(hunkData => (
                    <Box
                      key={hunkData.id}
                      borderWidth="1px"
                      borderStyle="solid"
                      borderColor={tokens.stroke.neutral[5]}
                      borderRadius="large"
                      overflow="hidden"
                      mb={2}
                    >
                      <DiffHunkCardV3 hunkData={hunkData} />
                    </Box>
                  ))
                ) : (
                  <GenericCodeBlockV3 language="python">{metadata.body}</GenericCodeBlockV3>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </>
    );
  }

  // Create path
  return (
    <>
      {metadataBox}
      {metadata?.description != null && (
        <CollapsibleFieldV3 label="Description" value={metadata.description} />
      )}
      {metadata?.runbook != null && <CollapsibleFieldV3 label="Runbook" value={metadata.runbook} />}
      {metadata?.reference != null && (
        <CollapsibleFieldV3 label="Reference" value={metadata.reference} />
      )}
      {metadata?.body != null && (
        <Box overflow="hidden">
          <AbstractButton onClick={() => setShowCode(!showCode)}>
            <Icon type={showCode ? 'chevron-down' : 'chevron-right'} size="small" />
            Detection Code
          </AbstractButton>
          <Collapse isOpen={showCode}>
            <Box
              mt={2}
              maxHeight="400px"
              overflowY="auto"
              borderWidth="1px"
              borderStyle="solid"
              borderColor={tokens.stroke.neutral[5]}
              borderRadius="large"
              overflow="hidden"
            >
              {/* Ugly hack to remove the top margin from the code block that is inherited from syntax highlighter */}
              <Box my="-0.5em">
                <GenericCodeBlockV3 language="python">{metadata.body}</GenericCodeBlockV3>
              </Box>
            </Box>
          </Collapse>
        </Box>
      )}
    </>
  );
};

interface QuestionOption {
  label: string;
  value: string;
}

const emptyOptions: QuestionOption[] = [];

const QuestionContent = ({
  metadata,
  answer,
  onAnswerChange,
  onSubmit,
  resolved,
}: {
  metadata: any;
  answer: string | string[];
  onAnswerChange: (answer: string | string[]) => void;
  onSubmit?: () => void;
  resolved?: boolean;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const question = metadata?.question;
  const isMulti = metadata?.response_type === 'multi_select';
  const options: QuestionOption[] = metadata?.options || emptyOptions;
  const questionContext = metadata?.context;
  const [isOtherSelected, setIsOtherSelected] = React.useState(false);
  const [otherText, setOtherText] = React.useState('');

  const isOptionSelected = (value: string) => {
    if (isMulti) {
      return Array.isArray(answer) && answer.includes(value);
    }
    return !isOtherSelected && answer === value;
  };

  const handleOptionClick = (value: string) => {
    if (resolved) {
      return;
    }
    if (isMulti) {
      const current = Array.isArray(answer) ? answer : [];
      onAnswerChange(
        current.includes(value) ? current.filter(v => v !== value) : [...current, value],
      );
    } else {
      setIsOtherSelected(false);
      setOtherText('');
      onAnswerChange(value);
    }
  };

  const handleOtherClick = () => {
    if (resolved) {
      return;
    }
    if (isMulti && isOtherSelected) {
      // Deselecting "Other" in multi_select — remove custom text from answers
      setIsOtherSelected(false);
      setOtherText('');
      const optionValues = options.map(o => o.value);
      const current = Array.isArray(answer) ? answer : [];
      onAnswerChange(current.filter(v => optionValues.includes(v)));
    } else if (!isOtherSelected) {
      setIsOtherSelected(true);
      if (!isMulti) {
        onAnswerChange('');
      }
    }
  };

  const handleOtherTextChange = (text: string) => {
    setOtherText(text);
    if (isMulti) {
      const optionValues = options.map(o => o.value);
      const current = Array.isArray(answer) ? answer : [];
      const predefined = current.filter(v => optionValues.includes(v));
      onAnswerChange(text.trim() ? [...predefined, text] : predefined);
    } else {
      onAnswerChange(text);
    }
  };

  const iconType = (selected: boolean) => {
    if (isMulti) {
      return selected ? 'checkbox-selected' : 'checkbox';
    }
    return selected ? 'radio-selected' : 'radio';
  };

  const selectedColor = tokens.content.text.semantic.success.base;
  const unselectedColor = tokens.content.text.neutral[3];

  return (
    <Flex direction="column" gap={3}>
      <Text variant="body/md">{question}</Text>
      {questionContext && (
        <Text variant="body/sm" color={unselectedColor}>
          {questionContext}
        </Text>
      )}
      <Flex
        direction="column"
        gap={2}
        sx={resolved ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      >
        {options.map(option => {
          const selected = isOptionSelected(option.value);
          return (
            <Card
              key={option.value}
              p={3}
              variant={selected ? 'mid' : 'midToBrand'}
              sx={{ cursor: resolved ? 'default' : 'pointer' }}
              onClick={() => handleOptionClick(option.value)}
            >
              <Flex align="center" gap={2}>
                <Icon
                  type={iconType(selected)}
                  size="small"
                  color={selected ? selectedColor : unselectedColor}
                />
                <Text variant="body/sm">{option.label}</Text>
              </Flex>
            </Card>
          );
        })}
        <Card
          p={3}
          variant={isOtherSelected ? 'mid' : 'midToBrand'}
          sx={{ cursor: resolved ? 'default' : 'pointer' }}
          onClick={handleOtherClick}
        >
          <Flex align="center" gap={2}>
            <Icon
              type={iconType(isOtherSelected)}
              size="small"
              color={isOtherSelected ? selectedColor : unselectedColor}
            />
            <Text variant="body/sm">Other</Text>
          </Flex>
          {isOtherSelected && (
            <Box mt={2} ml="28px">
              <TextInput
                autoFocus
                label=""
                value={otherText}
                onClick={e => e.stopPropagation()}
                onChange={e => handleOtherTextChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && onSubmit && otherText.trim()) {
                    e.preventDefault();
                    onSubmit();
                  }
                }}
                placeholder="Type your answer..."
              />
            </Box>
          )}
        </Card>
      </Flex>
    </Flex>
  );
};

// TODO: Need a way to add TS types for toolInvocation.metadata. Those are not generated by the schema.
const ToolInvocationCard = ({
  toolInvocation,
  toolName,
  onApprove,
  onApproveWithMetadata,
  onReject,
  isLoading,
}: {
  toolInvocation: AIToolInvocation | null;
  toolName: string;
  onApprove: () => void;
  onApproveWithMetadata?: (metadata: Record<string, unknown>) => void;
  onReject: (reason?: string) => void;
  isLoading: boolean;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const isGenerated = !!toolInvocation;
  const isApproved = !!toolInvocation?.approvedAt;
  const isRejected = !!toolInvocation?.rejectedAt;
  const isTimedOut = !!toolInvocation?.timedOutAt;
  const [expanded, setExpanded] = React.useState(!isLoading);

  // Expand the card when the tool output is finished
  React.useEffect(() => {
    if (isGenerated) {
      setExpanded(true);
    }
  }, [isGenerated]);

  const [showRejectFeedback, setShowRejectFeedback] = React.useState(false);
  const [rejectFeedback, setRejectFeedback] = React.useState('');
  const [showBreathingAnimation, setShowBreathingAnimation] = React.useState(false);
  const [questionAnswer, setQuestionAnswer] = React.useState<string | string[]>(
    toolInvocation?.metadata?.response_type === 'multi_select' ? [] : '',
  );

  const isQuestionTool = toolName === UTILITIES_ASK_QUESTION;

  const isPendingApproval = isGenerated && !isApproved && !isRejected && !isTimedOut;

  // Collapse the card when the tool output is decided
  React.useEffect(() => {
    if (isRejected || isTimedOut || isApproved) {
      setExpanded(false);
      setShowRejectFeedback(false);
      setRejectFeedback('');
      setShowBreathingAnimation(false);
    }
  }, [isApproved, isRejected, isTimedOut]);

  // Start breathing animation when pending approval, stop after duration
  React.useEffect(() => {
    if (isPendingApproval) {
      setShowBreathingAnimation(true);
      const timer = setTimeout(() => {
        setShowBreathingAnimation(false);
      }, BREATHING_ANIMATION_DURATION_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isPendingApproval]);

  const handleRejectClick = () => {
    setShowRejectFeedback(true);
  };

  const handleCancelReject = () => {
    setShowRejectFeedback(false);
    setRejectFeedback('');
  };

  const handleConfirmReject = () => {
    onReject(rejectFeedback.trim() || undefined);
  };

  const handleQuestionSubmit = React.useCallback(() => {
    if (
      isLoading ||
      isApproved ||
      isRejected ||
      isTimedOut ||
      !onApproveWithMetadata ||
      questionAnswer === '' ||
      (Array.isArray(questionAnswer) && questionAnswer.length === 0)
    ) {
      return;
    }
    onApproveWithMetadata({ answer: questionAnswer });
  }, [isLoading, isApproved, isRejected, isTimedOut, questionAnswer, onApproveWithMetadata]);

  const renderToolContent = React.useCallback(() => {
    switch (toolName) {
      case DETECTIONS_CREATE:
      case DETECTIONS_UPDATE:
        return <DetectionContent metadata={toolInvocation?.metadata} />;
      case ALERTS_UPDATE:
        return <UpdateAlertContent metadata={toolInvocation?.metadata} />;
      case ALERTS_ASSIGN:
        return <AssignAlertContent metadata={toolInvocation?.metadata} />;
      case ALERTS_BULK_UPDATE:
        return <BulkUpdateAlertsContent metadata={toolInvocation?.metadata} />;
      case ALERTS_ADD_COMMENT:
        return <AddAlertCommentContent metadata={toolInvocation?.metadata} />;
      case UTILITIES_FETCH_WEB:
        return <WebContent metadata={toolInvocation?.metadata} />;
      case DATALAKE_WRITE_SAVED_QUERY:
        return <SavedQueryContent metadata={toolInvocation?.metadata} />;
      case NOTIFICATIONS_SLACK_SEND_MESSAGE:
        return <SlackSendMessageContent metadata={toolInvocation?.metadata} />;
      case UTILITIES_ASK_QUESTION:
        return (
          <QuestionContent
            metadata={toolInvocation?.metadata}
            answer={questionAnswer}
            onAnswerChange={setQuestionAnswer}
            onSubmit={handleQuestionSubmit}
            resolved={isApproved || isRejected || isTimedOut}
          />
        );
      default:
        return <GenericToolInputContent metadata={toolInvocation?.metadata} />;
    }
  }, [
    toolName,
    toolInvocation?.metadata,
    questionAnswer,
    handleQuestionSubmit,
    isApproved,
    isRejected,
    isTimedOut,
  ]);

  const headerText = React.useMemo(() => {
    switch (toolName) {
      case DETECTIONS_CREATE:
        if (!isGenerated) {
          return 'Creating detection...';
        }
        return toolInvocation?.metadata?.displayName || 'Detection';
      case DETECTIONS_UPDATE: {
        const meta = toolInvocation?.metadata;
        if (!isGenerated) {
          return 'Updating detection...';
        }
        return (
          meta?._existing?.displayName || (meta?.id ? `Update: ${meta.id}` : 'Update Detection')
        );
      }
      case ALERTS_UPDATE: {
        if (!isGenerated) {
          return 'Preparing alert update...';
        }
        const { status, quality, context_tags: contextTags } = toolInvocation?.metadata || {};
        const operations: string[] = [];
        if (status) {
          operations.push(`status to ${status}`);
        }
        if (quality) {
          operations.push(`quality to ${quality}`);
        }
        if (contextTags?.length) {
          operations.push('context tags');
        }
        return operations.length > 0 ? `Update Alert: ${operations.join(', ')}` : 'Update Alert';
      }
      case ALERTS_ASSIGN:
        return !isGenerated ? 'Preparing assignment...' : 'Assign Alert';
      case ALERTS_BULK_UPDATE: {
        if (!isGenerated) {
          return 'Preparing bulk alert update...';
        }
        const { status, quality, context_tags: rawContextTags } = toolInvocation?.metadata || {};
        const contextTags = normalizeContextTags(rawContextTags);
        const alertIds = toolInvocation?.metadata?.alert_ids as string[] | undefined;
        const count = alertIds?.length || 0;
        const operations: string[] = [];
        if (status) {
          operations.push(`status to ${status}`);
        }
        if (quality) {
          operations.push(`quality to ${quality}`);
        }
        if (contextTags?.length) {
          operations.push(`tags: ${contextTags.join(', ')}`);
        }
        const opsText = operations.length > 0 ? ` (${operations.join(', ')})` : '';
        return `Bulk Update ${count} Alert${count !== 1 ? 's' : ''}${opsText}`;
      }
      case ALERTS_ADD_COMMENT:
        return !isGenerated ? 'Preparing comment...' : 'Add Comment to Alert';
      case UTILITIES_FETCH_WEB:
        return !isGenerated ? 'Fetching web content...' : 'Web Content Request';
      case DATALAKE_WRITE_SAVED_QUERY: {
        if (!isGenerated) {
          return 'Saving query...';
        }
        const queryName = toolInvocation?.metadata?.name;
        const queryType = toolInvocation?.metadata?.type;
        return queryName || (queryType === 'scheduled' ? 'Scheduled Query' : 'Saved Query');
      }
      case NOTIFICATIONS_SLACK_SEND_MESSAGE:
        if (!isGenerated) {
          return 'Sending Slack message...';
        }
        return toolInvocation?.metadata?.thread_ts ? 'Reply in Slack Thread' : 'Send Slack Message';
      case UTILITIES_ASK_QUESTION:
        return !isGenerated ? 'Waiting for your input...' : 'Follow-up Question';
      default:
        return !isGenerated ? 'Invoking tool...' : toolName;
    }
  }, [toolName, isGenerated, toolInvocation?.metadata]);

  const statusColor = React.useMemo(() => {
    if (isApproved) {
      return tokens.content.text.semantic.success.base;
    }
    if (isTimedOut) {
      return tokens.content.text.semantic.suggest.base;
    }
    return tokens.content.text.semantic.error.base;
  }, [
    isApproved,
    isTimedOut,
    tokens.content.text.semantic.success.base,
    tokens.content.text.semantic.suggest.base,
    tokens.content.text.semantic.error.base,
  ]);

  const statusText = React.useMemo(() => {
    if (isApproved) {
      return 'Approved';
    }
    if (isTimedOut) {
      return 'Timed Out';
    }
    return 'Rejected';
  }, [isApproved, isTimedOut]);

  const rejectFeedbackPlaceholder = React.useMemo(() => {
    switch (toolName) {
      case DETECTIONS_CREATE:
      case DETECTIONS_UPDATE:
        return 'e.g., Change the severity to HIGH, add more log types, adjust the threshold...';
      case ALERTS_UPDATE:
        return 'e.g., Change to TRIAGED instead, add more context to the comment...';
      case ALERTS_ASSIGN:
        return 'e.g., Assign to a different user, add a comment explaining why...';
      case ALERTS_BULK_UPDATE:
        return 'e.g., Only update CRITICAL alerts, change the status to TRIAGED instead...';
      case ALERTS_ADD_COMMENT:
        return 'e.g., Make the comment more detailed, include specific indicators...';
      case UTILITIES_FETCH_WEB:
        return 'e.g., Use a different URL, try a different approach...';
      case DATALAKE_WRITE_SAVED_QUERY:
        return 'e.g., Change the schedule frequency, use a different query name, adjust the SQL...';
      case NOTIFICATIONS_SLACK_SEND_MESSAGE:
        return 'e.g., Send to a different channel, adjust the message content...';
      case UTILITIES_ASK_QUESTION:
        return 'e.g., Ask a different question, skip this step...';
      default:
        return 'Suggest what the AI should do differently...';
    }
  }, [toolName]);

  return (
    <Card
      px={4}
      py={2}
      display="flex"
      flexDirection="column"
      overflowY="hidden"
      sx={
        showBreathingAnimation
          ? {
              animation: 'breathingBorder 1.5s ease-in-out infinite',
              '@keyframes breathingBorder': {
                '0%, 100%': {
                  borderColor: tokens.stroke.neutral[3],
                  boxShadow: `0 0 0 0 transparent`,
                },
                '50%': {
                  borderColor: tokens.stroke.semantic.info.base,
                  boxShadow: `0 0 8px 2px ${tokens.background.semantic.info[2]}`,
                },
              },
            }
          : undefined
      }
    >
      {!isGenerated ? (
        <Flex align="center" gap={2}>
          <Icon type="sparkle" size="medium" color={tokens.content.assets.accent[1].base} />
          <Text py={2} fontSize="large" fontWeight="bold">
            {headerText}
          </Text>
          <Spinner size="small" />
        </Flex>
      ) : (
        <>
          <Flex align="center" gap={2} justify="space-between">
            <Flex align="center" gap={2}>
              <AbstractButton
                onClick={() => setExpanded(!expanded)}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Icon
                  type={expanded ? 'chevron-down' : 'chevron-right'}
                  size="medium"
                  color={tokens.content.assets.neutral.base}
                />
                <Text py={2} variant="body/lg" fontWeight="bold">
                  {headerText}
                </Text>
              </AbstractButton>
              {isApproved && (
                <Icon
                  type="check-circle"
                  size="small"
                  color={tokens.content.text.semantic.success.base}
                />
              )}
              {isRejected && (
                <Icon
                  type="close-circle"
                  size="small"
                  color={tokens.content.text.semantic.error.base}
                />
              )}
              {isTimedOut && (
                <Icon type="clock" size="small" color={tokens.content.text.semantic.suggest.base} />
              )}
              {isLoading && <Spinner size="small" />}
            </Flex>
          </Flex>
          <Collapse isOpen={expanded} duration={300}>
            <Flex direction="column" gap={3} mt={2}>
              {/* Hide question content when in skip-feedback mode */}
              {!(isQuestionTool && showRejectFeedback) && renderToolContent()}
              {!isApproved && !isRejected && !isTimedOut ? (
                <Flex direction="column" gap={3} pb={4}>
                  {showRejectFeedback ? (
                    <>
                      <TextInput
                        autoFocus
                        label="Tell Panther AI what to do differently"
                        value={rejectFeedback}
                        onChange={e => setRejectFeedback(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleConfirmReject();
                          }
                        }}
                        placeholder={rejectFeedbackPlaceholder}
                        disabled={isLoading}
                      />
                      <Flex justify="flex-end" spacing={2}>
                        <Button
                          size="medium"
                          onClick={handleCancelReject}
                          disabled={isLoading}
                          variant="secondary"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="medium"
                          onClick={handleConfirmReject}
                          disabled={isLoading}
                          variant={isQuestionTool ? 'primary' : 'destructive'}
                        >
                          {isQuestionTool ? 'Send' : 'Confirm Reject'}
                        </Button>
                      </Flex>
                    </>
                  ) : (
                    <Flex justify="flex-end" spacing={2}>
                      <Button
                        size="medium"
                        onClick={handleRejectClick}
                        disabled={isLoading || isApproved || isRejected || isTimedOut}
                        variant="secondary"
                      >
                        {isQuestionTool ? 'Skip' : 'Reject'}
                      </Button>
                      <Button
                        onClick={isQuestionTool ? handleQuestionSubmit : onApprove}
                        disabled={
                          isLoading ||
                          isApproved ||
                          isRejected ||
                          isTimedOut ||
                          (isQuestionTool &&
                            (questionAnswer === '' ||
                              (Array.isArray(questionAnswer) && questionAnswer.length === 0)))
                        }
                        size="medium"
                      >
                        {isQuestionTool ? 'Submit' : 'Accept'}
                      </Button>
                    </Flex>
                  )}
                </Flex>
              ) : (
                <Flex justify="flex-end" pb={4}>
                  <Text color={statusColor} fontWeight="medium">
                    {statusText}
                  </Text>
                </Flex>
              )}
            </Flex>
          </Collapse>
        </>
      )}
    </Card>
  );
};

/**
 * Hook to scroll an element into view after its content expands.
 *
 * The Collapse component animates with a fixed 300ms duration, so we wait 400ms
 * before scrolling to ensure the animation has completed.
 */
const useScrollIntoViewOnExpand = (shouldScroll: boolean) => {
  const scrollAnchorRef = React.useRef<HTMLDivElement>(null);
  const hasScrolledRef = React.useRef(false);

  React.useEffect(() => {
    if (!shouldScroll) {
      hasScrolledRef.current = false;
      return undefined;
    }

    if (hasScrolledRef.current) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (!hasScrolledRef.current) {
        hasScrolledRef.current = true;
        scrollAnchorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [shouldScroll]);

  return { scrollAnchorRef };
};

/** Wrapper that auto-scrolls the card into view when pending approval */
const ToolInvocationCardWithScroll = (props: Parameters<typeof ToolInvocationCard>[0]) => {
  const isPendingApproval =
    !!props.toolInvocation &&
    !props.toolInvocation.approvedAt &&
    !props.toolInvocation.rejectedAt &&
    !props.toolInvocation.timedOutAt;

  const { scrollAnchorRef } = useScrollIntoViewOnExpand(isPendingApproval);

  return (
    <>
      <ToolInvocationCard {...props} />
      {/* Invisible anchor for scrollIntoView to ensure buttons are visible - only render when pending */}
      {isPendingApproval && <div ref={scrollAnchorRef} style={{ height: 8 }} />}
    </>
  );
};

const ToolInvocationV3 = ({
  toolInvocationId,
  toolName,
}: {
  toolInvocationId: string;
  toolName: string;
}) => {
  const toolInvocationCtx = useToolInvocationContext();
  const genericCtx = useGenericAISummaryContextV3();
  const {
    toolInvocations = [],
    streamId,
    isCancelled,
  } = toolInvocationCtx ?? genericCtx ?? { toolInvocations: [], streamId: '', isCancelled: false };
  const { pushSnackbar } = useSnackbar();
  const [hasDecided, setHasDecided] = React.useState(false);
  const [decideToolInvocation, { loading }] = useDecideToolInvocation({
    onError: error => {
      setHasDecided(false);
      pushSnackbar({
        variant: 'error',
        title: 'Error approving tool invocation',
        description: extractErrorMessage(error),
      });
    },
  });

  const toolInvocation = toolInvocations.find(i => i.id === toolInvocationId) ?? null;
  const isServerDecided =
    !!toolInvocation?.approvedAt || !!toolInvocation?.rejectedAt || !!toolInvocation?.timedOutAt;

  if (isCancelled) {
    return null;
  }

  return (
    <ToolInvocationCardWithScroll
      toolInvocation={toolInvocation}
      toolName={toolName}
      onApprove={() => {
        setHasDecided(true);
        decideToolInvocation({
          variables: { input: { streamId, toolInvocationId, approved: true } },
        });
      }}
      onApproveWithMetadata={metadata => {
        setHasDecided(true);
        decideToolInvocation({
          variables: {
            input: {
              streamId,
              toolInvocationId,
              approved: true,
              metadata,
            },
          },
        });
      }}
      onReject={reason => {
        setHasDecided(true);
        decideToolInvocation({
          variables: { input: { streamId, toolInvocationId, approved: false, reason } },
        });
      }}
      isLoading={loading || (hasDecided && !isServerDecided)}
    />
  );
};

export default React.memo(ToolInvocationV3);
